import dayjs from '../../dayjs/dayjs_timezone';
import {
  convertFramesToShopTimezone,
  flattenFramesToShopTimezone,
  getFrames,
  getBlocks,
  getCourseFrames,
} from '../services/api';
import { framesActions } from '../reducers/frames';

const framesInTimeZone = (frames, timezone) => {
  const newFrames = [];
  if (!frames) return null;

  frames.forEach((frame) => {
    frame.timeslots.forEach((timeslot) => {
      const date = dayjs(timeslot.start).tz(timezone).format('YYYY-MM-DD');
      const newFrame = newFrames.find((f) => f.date === date);

      if (newFrame) {
        newFrame.timeslots.push(timeslot);
      } else {
        newFrames.push({
          date,
          timeslots: [timeslot],
        });
      }
    });
  });

  return newFrames;
};

const blocksInTimeZone = (blocks, timezone) => {
  const newBlocks = [];
  blocks.forEach((block) => {
    block.timeslots.forEach((timeslot) => {
      const includeFinish = block.hasOwnProperty('finish_available_slot_count');
      const date = dayjs(timeslot.start).tz(timezone).format('YYYY-MM-DD');
      let newBlock = newBlocks.find((b) => b.date === date);
      if (!newBlock) {
        newBlock = {
          date,
          timeslots: [],
          available_slot_count: 0,
        };
        if (includeFinish) newBlock.finish_available_slot_count = 0;
        newBlocks.push(newBlock);
      }
      newBlock.timeslots.push(timeslot);
      newBlock.available_slot_count += (timeslot.status === 1 ? 1 : 0);
      if (includeFinish) newBlock.finish_available_slot_count += (timeslot.finish_status === 1 ? 1 : 0);
    });
  });

  return newBlocks;
};

const finishBlocks = (blocks) => {
  if (!blocks[0]?.hasOwnProperty('finish_available_slot_count')) return blocks;

  return blocks.map((block) => {
    const newBlock = { ...block };
    newBlock.available_slot_count = newBlock.finish_available_slot_count;
    newBlock.timeslots = newBlock.timeslots.map((slot) => {
      const newSlot = { ...slot };
      newSlot.availability = newSlot.finish_available || newSlot.availability;
      newSlot.status = newSlot.finish_status || newSlot.status;
      return newSlot;
    });
    return newBlock;
  });
};

export const {
  fetchBlocksLoading,
  fetchFrameLoading,
  resetFirstOccurrence,
  setAvailableBlocks,
  setBlackouts,
  setFinishAvailableBlocks,
  setFinishBlackouts,
  setFinishSelection,
  setFinishTimeSlots,
  setFrames,
  setFramesFrames,
  setGotPage,
  setIsRentalFlag,
  setTimeSlots,
  setTimeSlotsLoading,
  setVariantSelected,
} = framesActions;

export const setBlocks = (blocks, timezone, isConcat) => {
  const blocksInTz = blocksInTimeZone(blocks, timezone);

  return (dispatch, getState) => {
    dispatch(setTimeSlots({ value: blocksInTz, isConcat }));
    dispatch(setBlackouts({ value: blocksInTz, isConcat }));
    dispatch(setAvailableBlocks({ value: blocksInTz, isConcat }));

    const state = getState();
    if (state.frames.isRental) {
      const fblocks = finishBlocks(blocksInTz);
      dispatch(setFinishTimeSlots({ value: fblocks, isConcat }));
      dispatch(setFinishBlackouts({ value: fblocks, isConcat }));
      dispatch(setFinishAvailableBlocks({ value: fblocks, isConcat }));
    }
  };
};

export const fetchCourseFrames = (params, location_id) => (dispatch, getState) => {
  if (params.length === 0) return null;

  dispatch(fetchFrameLoading(true));
  return new Promise(async (resolve, reject) => {
    let data = {};
    const starts = params.map((e) => e.start);
    const finishes = params.map((e) => e.finish);
    const start = new Date(Math.min.apply(null, starts));
    const finish = new Date(Math.max.apply(null, finishes));
    const { frames: { cachedParams } } = getState();
    const { timeZone } = window.bta;
    const cachedFrames = cachedParams.find((x) => dayjs(x.start).isSame(dayjs(start))
      && dayjs(x.finish).isSame(dayjs(finish))
      && x.location_id === location_id);

    if (!cachedFrames || window.bta.test) {
      try {
        data = await getCourseFrames({
          external_id: window.bta.externalId,
          location_id,
          start,
          finish,
        });
      } catch (error) {
        dispatch(fetchFrameLoading(false));
        reject(error);
      }
      dispatch(setGotPage({
        start, finish, location_id, frames: data.frames,
      }));
    } else {
      data = {
        frames: convertFramesToShopTimezone(cachedFrames.frames, timeZone),
        flattenTimeSlots: flattenFramesToShopTimezone(cachedFrames.frames, timeZone),
      };
    }

    dispatch(setFrames(data));
    dispatch(fetchFrameLoading(false));
    resolve(data);
  });
};

export const fetchFrames = (
  params,
  cached = true,
) => (dispatch, getState) => {
  dispatch(fetchFrameLoading(true));
  return new Promise((resolve, reject) => {
    const {
      frames: { cachedParams },
      settings: { timeZone },
    } = getState();

    const {
      start, finish, location_id, resource_id, isEventsList,
    } = params;
    const cachedFrames = cachedParams.find((x) => dayjs(x.start).isSame(start)
      && dayjs(x.finish).isSame(finish) && x.location_id === location_id
      && x.resource_id === resource_id);

    if (!cached || !cachedFrames || window.bta.test) {
      getFrames({
        external_id: window.bta.externalId,
        start,
        finish,
        location_id,
        resource_id,
        is_events_list: isEventsList,
      }).then((data) => {
        const framesInTz = framesInTimeZone(data.frames, timeZone);
        dispatch(setGotPage({
          start, finish, location_id, nextOccurrence: data.nextOccurrence, frames: framesInTz,
        }));
        dispatch(setFrames({ ...data, frames: framesInTz }));
        dispatch(fetchFrameLoading(false));
        resolve({ ...data, frames: framesInTz });
      }).catch((error) => {
        dispatch(fetchFrameLoading(false));
        reject(error);
      });
    } else {
      const framesInTz = framesInTimeZone(cachedFrames.frames, timeZone);
      dispatch(setFrames({ frames: framesInTz }));
      dispatch(fetchFrameLoading(false));
      resolve({ nextOccurrence: cachedFrames.nextOccurrence, frames: framesInTz });
    }
  });
};

export const fetchBlocks = ({
  externalId,
  finish,
  interval,
  isConcat = false,
  locationId = [],
  resourceId = [],
  timezone,
  start,
  withFinishAvailability,
}) => (dispatch) => {
  if (!externalId) return; // if externalId is null or undefined then return.

  dispatch(setTimeSlotsLoading(true));

  let locationIds = locationId;
  let resourceIds = resourceId;

  if (!Array.isArray(locationId) && !!locationId) locationIds = [locationId];
  if (!Array.isArray(resourceId) && !!resourceId) resourceIds = [resourceId];

  dispatch(setVariantSelected(externalId));

  // eslint-disable-next-line consistent-return
  return new Promise((resolve, reject) => {
    getBlocks({
      start,
      finish,
      interval,
      external_id: externalId,
      location_ids: locationIds,
      resource_ids: resourceIds,
      with_finish_availability: withFinishAvailability,
    }).then((data) => {
      const blocks = data.blocks ? data.blocks : [];
      dispatch(setTimeSlotsLoading(false));
      dispatch(setBlocks(blocks, timezone, isConcat));
      resolve(data);
    }).catch((error) => {
      dispatch(setTimeSlotsLoading(false));
      reject(error);
    });
  });
};

export const changeFramesTimezone = (timezone) => (dispatch, getState) => {
  const { frames: { frames } } = getState();
  dispatch(setFramesFrames(framesInTimeZone(frames, timezone)));
};
